import { Component } from '@theme/component';

/**
 * A custom element that counts down to a deadline.
 *
 * The deadline is passed as an ISO 8601 string on the `end` attribute, including
 * an explicit UTC offset (e.g. `2026-08-01T00:00:00+02:00`). The remaining time is
 * computed in the browser rather than in Liquid because storefront pages are cached
 * and a server-rendered value would be stale.
 *
 * Once the deadline passes the timer stops and holds at 00:00:00:00.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<Refs>}
 */
export class CountdownTimer extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /**
   * The interval ID for the per-second update.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.#tick();
    this.#interval = setInterval(this.#tick, 1000);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * The deadline in milliseconds since the epoch, or NaN when the `end`
   * attribute is missing or unparseable.
   */
  get endTime() {
    return Date.parse(this.getAttribute('end') ?? '');
  }

  get expired() {
    return this.hasAttribute('expired');
  }

  /**
   * Background tabs throttle timers, so the display can drift. Recompute on return.
   */
  #handleVisibilityChange = () => {
    if (!document.hidden) this.#tick();
  };

  #tick = () => {
    const end = this.endTime;

    if (Number.isNaN(end)) {
      this.#render(0, 0, 0, 0);
      this.#stop();
      return;
    }

    const remaining = end - Date.now();

    if (remaining <= 0) {
      this.#render(0, 0, 0, 0);
      this.#stop();
      this.toggleAttribute('expired', true);
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);

    this.#render(
      Math.floor(totalSeconds / 86400),
      Math.floor(totalSeconds / 3600) % 24,
      Math.floor(totalSeconds / 60) % 60,
      totalSeconds % 60
    );
  };

  #stop() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * @param {number} days
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   */
  #render(days, hours, minutes, seconds) {
    const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');
    const next = { days: pad(days), hours: pad(hours), minutes: pad(minutes), seconds: pad(seconds) };

    for (const [name, value] of Object.entries(next)) {
      const element = this.refs[/** @type {keyof Refs} */ (name)];

      if (element.textContent !== value) element.textContent = value;
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
